import React from "react";
import { Button } from "primereact/button";
import ColorPicker from "components/basic/color-picker/ColorPicker";
import { DatasetDropdown } from "components/basic/dropdowns/dataset/DatasetDropdown";
import { Divider } from "primereact/divider";
import { DropdownChangeEvent } from "primereact/dropdown";
import { easasConfirmDialog } from "components/fragments/easas-global-fragment/EasasGlobalConfirmDialog";
import { InputNumber, InputNumberValueChangeEvent } from "primereact/inputnumber";
import { InputSwitch, InputSwitchChangeEvent } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import { T } from "services/lang/LanguageService";
import TimeSpanInputs from "components/fragments/product-template-configurator/ProductTemplateAttributesConfiguration/type-fields/components/TimeSpanInputs";
import { SparkLineProps } from "./SparkLineTypes";
import LoadConfigInputs, { DefaultDatasetLoadingConf } from "../charts/LoadConfigInputs";
import { DatasetLoadingConf } from "../charts/interfaces";

export type SparkLineInputsProps = {
  currentConfig: SparkLineProps;
  onDatasetChange: (
    property: keyof SparkLineProps,
    value: string | number | boolean | null | DatasetLoadingConf | undefined
  ) => void;
  onConfigChange: (
    e:
      | InputNumberValueChangeEvent
      | React.ChangeEvent<HTMLInputElement>
      | InputSwitchChangeEvent
      | DropdownChangeEvent
  ) => void;
  onNumberChange: (input: string, value: number) => void;
  handleLoadConfChange: (updatedLoadConf: DatasetLoadingConf | undefined) => void;
  advanced?: boolean;
  isAdvancedOptions: boolean;
  setIsAdvancedOptions: (isAdvancedOptions: boolean) => void;
  isMultipleCharts?: boolean;
  handleSetDefaultProperties: () => void;
};

const SparkLineInputs: React.FC<SparkLineInputsProps> = ({
  currentConfig,
  onDatasetChange,
  onConfigChange,
  onNumberChange,
  handleLoadConfChange,
  advanced = false,
  isAdvancedOptions,
  setIsAdvancedOptions,
  isMultipleCharts,
  handleSetDefaultProperties,
}) => {
  const confirmSetDefaultParameters = () => {
    easasConfirmDialog({
      header: T("confirm-dialog.spark-line-editor.set-default-parameters"),
      message: T("confirm-dialog.spark-line-editor.do-you-want-to-set-all-parameters-to-default-values"),
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      confirmedCallback: () => handleSetDefaultProperties(),
    });
  };

  return (
    <>
      <div className="flex flex-row w-12 justify-content-between my-3 p-0 gap-3">
        <div className="flex flex-column gap-2 w-10">
          <label htmlFor="dataset">{T("word.dataset")}</label>
          <DatasetDropdown value={currentConfig.dataset} onChange={val => onDatasetChange("dataset", val)} />
        </div>
        <div className="flex flex-column gap-3 w-1">
          <label htmlFor="advancedOptions">{T("side-menu.advanced")}</label>
          <InputSwitch
            id="advancedOptions"
            checked={isAdvancedOptions}
            tooltip={T("label.show-advanced-options")}
            tooltipOptions={{ position: "bottom" }}
            onChange={e => setIsAdvancedOptions(e.value)}
          />
        </div>
      </div>
      <div className="flex flex-row justify-content-between">
        <div className="flex flex-row gap-2 w-10">
          <div className="w-5 md:w-3 flex flex-column">
            <label>{`${T("word.max")} ${T("word.age").toLowerCase()}`}</label>
            <div className="p-inputgroup">
              <TimeSpanInputs
                value={currentConfig.maxAge ?? 0}
                onChange={val => onDatasetChange("maxAge", val)}
              />
            </div>
          </div>
          {isMultipleCharts && (
            <div className="w-5 md:w-2 flex flex-column">
              <label htmlFor="chartTitle">{T("word.chart-title")}</label>
              <InputText
                id="chartTitle"
                className="p-inputtext-sm"
                value={currentConfig.chartTitle}
                onChange={e => onDatasetChange("chartTitle", e.target.value)}
              />
            </div>
          )}
          {isAdvancedOptions && (
            <>
              <div className="w-max flex flex-column">
                <label htmlFor="updateFrequency">{T("load-conf.update-frequency")}</label>
                <div className="p-inputgroup w-full">
                  <InputNumber
                    id="updateFrequency"
                    inputClassName="w-6"
                    className="p-inputtext-sm"
                    value={currentConfig.updateFrequency}
                    onChange={e => onNumberChange("updateFrequency", e.value as number)}
                  />
                  <span className="p-inputgroup-addon">s</span>
                </div>
              </div>
              <div className="w-max flex flex-column">
                <label htmlFor="downsampleInterval">{T("load-conf.downsample-interval")}</label>
                <div className="p-inputgroup w-full">
                  <InputNumber
                    id="downsampleInterval"
                    inputClassName="w-6"
                    className="p-inputtext-sm"
                    value={currentConfig.downsampleInterval}
                    onChange={e => onNumberChange("downsampleInterval", e.value as number)}
                  />{" "}
                  <span className="p-inputgroup-addon">s</span>
                </div>
              </div>
              <div className="w-max flex flex-column">
                <label htmlFor="useDecimalsFromHundred">{T("action.disable-no-decimals-from-100")}</label>
                <InputSwitch
                  id="useDecimalsFromHundred"
                  checked={currentConfig.useDecimalsFromHundred ?? false}
                  onChange={e => onConfigChange(e)}
                />
              </div>
            </>
          )}
        </div>
        {isAdvancedOptions && (
          <div className="flex align-items-center w-1">
            <Button
              icon="pi pi-sync"
              onClick={confirmSetDefaultParameters}
              className="component-editor-button icon-blue h-full"
              tooltip={T("action.use-default-parameters")}
              tooltipOptions={{ position: "bottom" }}
            />
          </div>
        )}
      </div>

      {isAdvancedOptions && (
        <>
          <Divider className="m-1" />
          {!isMultipleCharts && (
            <>
              <div className="w-5 md:w-2 flex flex-column">
                <label htmlFor="sparkLineWidth">{T("config.spark-line.chart-width")}</label>
                <InputNumber
                  id="sparkLineWidth"
                  className="p-inputtext-sm"
                  value={currentConfig.sparkLineWidth}
                  onChange={e => onNumberChange("sparkLineWidth", e.value as number)}
                />
              </div>
              <div className="w-5 md:w-2 flex flex-column">
                <label htmlFor="height">{`${T("word.height")} (px)}`}</label>
                <InputNumber
                  id="height"
                  className="p-inputtext-sm"
                  value={currentConfig.height ?? undefined}
                  onChange={e => onNumberChange("height", e.value as number)}
                />
              </div>
            </>
          )}
          <div className="w-5 md:w-2 flex flex-column">
            <label htmlFor="lineWidth">{T("label.line-width")}</label>
            <InputNumber
              id="lineWidth"
              inputClassName="w-full"
              className="p-inputtext-sm"
              suffix=" px"
              value={currentConfig.lineWidth}
              onChange={e => onNumberChange("lineWidth", e.value as number)}
            />
          </div>
          <div className="w-5 md:w-2 flex flex-column">
            <label htmlFor="labelFontSize">{T("config.spark-line.label-font-size")}</label>
            <InputText
              id="labelFontSize"
              type="text"
              className="p-inputtext-sm"
              value={currentConfig.labelFontSize}
              onChange={onConfigChange}
            />
          </div>
          <div className="w-5 md:w-2 flex flex-column">
            <label htmlFor="lineColor">{T("label.line-color")}</label>
            <ColorPicker id="lineColor" value={currentConfig.lineColor} onValueChange={onConfigChange} />
          </div>
          {advanced && (
            <div className="flex flex-column w-5 md:w-1 gap-2 align-items-center px-2">
              <label htmlFor="showYAxis">{T("word.y-axis")}</label>
              <InputSwitch
                id="showYAxis"
                className="p-inputtext-sm"
                checked={Boolean(currentConfig.showYAxis)}
                onChange={onConfigChange}
              />
            </div>
          )}
        </>
      )}
      {currentConfig.showYAxis && (
        <>
          <Divider className="m-1" />
          <div className="w-12 flex justify-content-between">
            <div className="flex flex-row gap-2 justify-content-start">
              <div className="flex flex-column gap-2">
                <label htmlFor="minYAxis">{T("label.min-y-axis-value")}</label>
                <InputNumber
                  id="minYAxis"
                  className="p-inputtext-sm"
                  value={currentConfig.minYAxis}
                  onChange={e => onNumberChange("minYAxis", e.value as number)}
                />
              </div>
              <div className="flex flex-column gap-2">
                <label htmlFor="maxYAxis">{T("label.max-y-axis-value")}</label>
                <InputNumber
                  id="maxYAxis"
                  className="p-inputtext-sm"
                  value={currentConfig.maxYAxis}
                  onChange={e => onNumberChange("maxYAxis", e.value as number)}
                />
              </div>
            </div>
          </div>
        </>
      )}
      <Divider className="m-1" />
      <div className="flex flex-row w-12 gap-2">
        <div className="flex flex-column gap-2">
          <label htmlFor="showValueChangeIndicator">{T("label.show-value-change-indicator")}</label>
          <InputSwitch
            id="showValueChangeIndicator"
            className="p-inputtext-sm"
            checked={Boolean(currentConfig.showValueChangeIndicator)}
            onChange={onConfigChange}
          />
        </div>
        <div className="flex flex-column gap-2">
          <label htmlFor="useDiffIndicatorColors">{T("action.use-diff-indicator-colors")}</label>
          <InputSwitch
            id="useDiffIndicatorColors"
            className="p-inputtext-sm"
            checked={Boolean(currentConfig.useDiffIndicatorColors)}
            onChange={onConfigChange}
          />
        </div>
        <div className="flex flex-column gap-2">
          <label htmlFor="diffNumFontSize">{T("config.spark-line.diff-number-font-size")}</label>
          <InputText
            id="diffNumFontSize"
            type="text"
            className="p-inputtext-sm"
            value={currentConfig.diffNumFontSize}
            onChange={onConfigChange}
          />
        </div>
        <div className="flex flex-column gap-2">
          <label htmlFor="diffArrowFontSize">{T("config.spark-line.diff-arrow-font-size")}</label>
          <InputText
            id="diffArrowFontSize"
            type="text"
            className="p-inputtext-sm"
            value={currentConfig.diffArrowFontSize}
            onChange={onConfigChange}
          />
        </div>
      </div>
      <Divider className="m-1" />
      <div className="flex flex-row w-12">
        {isAdvancedOptions && (
          <div className="min-w-max mp-2 flex flex-column gap-3">
            <label htmlFor="useLoadConfig">{T("action.use-load-config")}</label>
            <InputSwitch
              id="useLoadConfig"
              checked={currentConfig.useLoadConf ?? false}
              onChange={(e: InputSwitchChangeEvent) => {
                handleLoadConfChange(e.value ? DefaultDatasetLoadingConf : undefined);
              }}
            />
          </div>
        )}
        {currentConfig.useLoadConf ? (
          <div className="w-12 flex flex-row justify-content-between">
            <LoadConfigInputs
              loadConf={currentConfig.loadConf as DatasetLoadingConf}
              handleLoadConfChange={handleLoadConfChange}
            />
          </div>
        ) : (
          <div className="w-2 flex flex-column p-2 gap-3">
            <label htmlFor="count">{T("action.count")}</label>
            <InputNumber
              id="count"
              inputClassName="w-full"
              className="p-inputtext-sm"
              value={currentConfig.count}
              onChange={e => onNumberChange("count", e.value as number)}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default SparkLineInputs;
